// InventoryDashboard.jsx
// Main inventory dashboard with product list and expandable rows for items

import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import "react-bootstrap-table/css/react-bootstrap-table.css";
import InventoryAPI from "../../../services/inventoryService";
import { convertFromUTC } from '../../../util/convert_timezone';
import TransferInventoryModal from './TransferInventoryModal';
import ReceiveInventory from './ReceiveInventory';
import DemandPreviewDrawer from '../purchasing/DemandPreviewDrawer';
import { isMultiLocation, getLocationName, getLocationBadgeColor, isAllLocations as checkIsAllLocations } from "../../../util/locationUtils";
import ReactGA from 'react-ga4';
import { isTechnician } from '../../../util/permissions';

const DESKTOP_BREAKPOINT = 1024;

class InventoryDashboard extends Component {
    state = {
        typeId: this.props.typeId,
        products: [],
        categories: [],
        units: [],

        // Search/Filter
        searchTerm: "",
        selectedCategoryFilter: "All Categories",

        // Expanded rows for showing items
        expandedRows: [],
        productItems: {},

        // Add/Edit product modal
        showProductModal: false,
        editingProduct: null,
        productForm: {
            name: '',
            brand: '',
            category_id: '',
            reorder_point: 0,
            cost_per_unit: 0,
            retail_price: 0,
            wholesale_price: 0
        },
        showAddBrand: false,
        newBrand: '',
        showAddProduct: false,
        newProduct: '',

        // Adjust quantity modal
        showAdjustModal: false,
        adjustingItem: null,
        adjustQuantity: '',

        // Usage history modal
        showUsageModal: false,
        usageProduct: null,
        usageData: [],
        loadingUsage: false,

        // Allocated inventory map { productId: allocatedQty }
        allocatedMap: {},

        // Transfer modal
        showTransferModal: false,
        transferItem: null,

        // Demand preview drawer
        showDemandDrawer: false,
        demandProduct: null,

        // Receive inventory modal
        showReceiveModal: false,

        // Responsive state
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT,
        loading: true,
        saving: false,
        error: null
    };

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadData();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    loadData = async () => {
        try {
            const locationId = this.props.selectedLocationId || null;
            const [stockLevels, categories, units, allocatedData] = await Promise.all([
                InventoryAPI.getStockLevels(this.props.typeId, {}, locationId),
                InventoryAPI.getServiceCategories(),
                InventoryAPI.getUnitsOfMeasure(),
                InventoryAPI.getAllocatedInventory(this.props.typeId, locationId).catch(() => [])
            ]);

            // Build allocated map: { productId: allocatedQty }
            // Accumulate (+=) because "All Locations" may return per-location rows
            const allocatedMap = {};
            if (Array.isArray(allocatedData)) {
                allocatedData.forEach(item => {
                    allocatedMap[item.product_id] = (allocatedMap[item.product_id] || 0) + (parseFloat(item.allocated_quantity) || 0);
                });
            }

            this.setState({
                products: stockLevels || [],
                categories: categories || [],
                units: units || [],
                allocatedMap,
                loading: false
            });
        } catch (error) {
            console.error('Error loading inventory data:', error);
            this.setState({
                error: 'Failed to load inventory data. Please refresh the page.',
                loading: false
            });
        }
    };

    // =========================================================================
    // Metrics
    // =========================================================================

    // Helper to get stock quantity from product (handles different API field names)
    getProductQuantity = (p) => {
        return parseFloat(p.quantity_on_hand || p.total_quantity || p.quantity_in_stock || p.stock_quantity || p.quantity || 0);
    };

    getMetrics = () => {
        const { products, allocatedMap } = this.state;
        const totalProducts = products.length;
        const lowStockCount = products.filter(p => {
            const qty = this.getProductQuantity(p);
            const reorder = parseFloat(p.reorder_point) || 0;
            const productId = p.product_id || p.id;
            const allocated = parseFloat(allocatedMap[productId] || 0);
            const available = qty - allocated;
            return available <= 0 || (reorder > 0 && available <= reorder);
        }).length;
        const totalItems = products.reduce((sum, p) => sum + (parseInt(p.item_count) || 0), 0);

        return { totalProducts, lowStockCount, totalItems };
    };

    // =========================================================================
    // Search/Filter
    // =========================================================================

    handleSearchChange = (e) => {
        this.setState({ searchTerm: e.target.value });
    };

    handleCategoryFilterChange = (e) => {
        this.setState({ selectedCategoryFilter: e.target.value });
    };

    clearSearch = () => {
        this.setState({ searchTerm: "" });
    };

    // Aggregate per-location stock rows into one row per product (for "All Locations" view)
    aggregateProducts = (products) => {
        const map = {};
        products.forEach(p => {
            const pid = p.product_id || p.id;
            if (!map[pid]) {
                map[pid] = {
                    ...p,
                    // Normalize to quantity_on_hand for consistent display
                    quantity_on_hand: this.getProductQuantity(p),
                    item_count: parseInt(p.item_count) || 0,
                    usage_count: parseInt(p.usage_count) || 0
                };
            } else {
                const existing = map[pid];
                existing.quantity_on_hand = (parseFloat(existing.quantity_on_hand) || 0) + this.getProductQuantity(p);
                existing.item_count = (parseInt(existing.item_count) || 0) + (parseInt(p.item_count) || 0);
                existing.usage_count = (parseInt(existing.usage_count) || 0) + (parseInt(p.usage_count) || 0);
            }
        });
        return Object.values(map);
    };

    getFilteredProducts = () => {
        const { products, searchTerm, selectedCategoryFilter } = this.state;
        const { allLocs, isMultiLoc } = this.getRoleFlags();

        // Aggregate per-location rows into one row per product when viewing all locations
        let filtered = (isMultiLoc && allLocs) ? this.aggregateProducts(products) : [...products];

        // Filter by search term (handle both field naming conventions)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(p => {
                const name = (p.product_name || p.name || '').toLowerCase();
                const brand = (p.brand || '').toLowerCase();
                const sku = (p.sku || '').toLowerCase();
                return name.includes(term) || brand.includes(term) || sku.includes(term);
            });
        }

        // Filter by category
        if (selectedCategoryFilter !== "All Categories") {
            filtered = filtered.filter(p =>
                p.category_name === selectedCategoryFilter ||
                String(p.category_id) === selectedCategoryFilter
            );
        }

        // Sort by category, then by brand, then by product name
        filtered.sort((a, b) => {
            const categoryA = (a.category_name || '').toLowerCase();
            const categoryB = (b.category_name || '').toLowerCase();
            const categoryCompare = categoryA.localeCompare(categoryB);
            if (categoryCompare !== 0) return categoryCompare;

            const brandA = (a.brand || '').toLowerCase();
            const brandB = (b.brand || '').toLowerCase();
            const brandCompare = brandA.localeCompare(brandB);
            if (brandCompare !== 0) return brandCompare;

            const nameA = (a.product_name || a.name || '').toLowerCase();
            const nameB = (b.product_name || b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

        return filtered;
    };

    // =========================================================================
    // Expandable Rows
    // =========================================================================

    handleToggleExpand = async (productId) => {
        const { productItems } = this.state;
        const isExpanded = this.state.expandedRows.includes(productId);

        if (isExpanded) {
            this.setState(prev => ({
                expandedRows: prev.expandedRows.filter(id => id !== productId)
            }));
        } else {
            // Load items if not already loaded
            if (!productItems[productId]) {
                try {
                    const items = await InventoryAPI.getInventoryItems(productId, this.props.typeId, null, this.props.selectedLocationId);
                    this.setState(prev => ({
                        productItems: {
                            ...prev.productItems,
                            [productId]: items || []
                        },
                        expandedRows: [...prev.expandedRows, productId]
                    }));
                } catch (error) {
                    console.error('Error loading items:', error);
                    this.setState(prev => ({
                        expandedRows: [...prev.expandedRows, productId]
                    }));
                }
            } else {
                this.setState(prev => ({
                    expandedRows: [...prev.expandedRows, productId]
                }));
            }
        }
    };

    // =========================================================================
    // Product Modal
    // =========================================================================

    handleOpenAddProduct = () => {
        this.setState({
            showProductModal: true,
            editingProduct: null,
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProduct: '',
            productForm: {
                name: '',
                brand: '',
                category_id: '',
                reorder_point: 0,
                cost_per_unit: 0,
                retail_price: 0,
                wholesale_price: 0
            }
        });
    };

    handleOpenEditProduct = (product) => {
        this.setState({
            showProductModal: true,
            editingProduct: product,
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProduct: '',
            productForm: {
                name: product.product_name || product.name || '',
                brand: product.brand || '',
                category_id: product.category_id || '',
                reorder_point: product.reorder_point || 0,
                cost_per_unit: product.cost_per_unit || 0,
                retail_price: product.retail_price || 0,
                wholesale_price: product.wholesale_price || 0
            }
        });
    };

    handleCloseProductModal = () => {
        this.setState({
            showProductModal: false,
            editingProduct: null,
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProduct: ''
        });
    };

    // Get unique brands from existing products, filtered by category if provided
    getUniqueBrands = (categoryId = null) => {
        const { products } = this.state;
        let filteredProducts = products;

        // Filter by category if a category is selected
        if (categoryId) {
            filteredProducts = products.filter(p =>
                String(p.category_id) === String(categoryId)
            );
        }

        const brands = filteredProducts
            .map(p => p.brand)
            .filter(brand => brand && brand.trim())
            .filter((brand, index, self) => self.indexOf(brand) === index)
            .sort();
        return brands;
    };

    // Get unique product names filtered by category and brand
    getUniqueProductNames = (categoryId = null, brand = null) => {
        const { products } = this.state;
        let filteredProducts = products;

        // Filter by category
        if (categoryId) {
            filteredProducts = filteredProducts.filter(p =>
                String(p.category_id) === String(categoryId)
            );
        }

        // Filter by brand
        if (brand) {
            filteredProducts = filteredProducts.filter(p =>
                p.brand === brand
            );
        }

        const names = filteredProducts
            .map(p => p.product_name || p.name)
            .filter(name => name && name.trim())
            .filter((name, index, self) => self.indexOf(name) === index)
            .sort();
        return names;
    };

    handleBrandChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddBrand: true,
                showAddProduct: false,
                newProduct: '',
                productForm: { ...this.state.productForm, brand: '', name: '' }
            });
        } else {
            this.setState({
                showAddBrand: false,
                newBrand: '',
                showAddProduct: false,
                newProduct: '',
                productForm: { ...this.state.productForm, brand: value, name: '' }
            });
        }
    };

    handleNewBrandChange = (e) => {
        const value = e.target.value;
        this.setState({
            newBrand: value,
            productForm: { ...this.state.productForm, brand: value }
        });
    };

    handleCancelNewBrand = () => {
        this.setState({
            showAddBrand: false,
            newBrand: '',
            productForm: { ...this.state.productForm, brand: '' }
        });
    };

    handleProductNameChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddProduct: true,
                productForm: { ...this.state.productForm, name: '' }
            });
        } else {
            this.setState({
                showAddProduct: false,
                newProduct: '',
                productForm: { ...this.state.productForm, name: value }
            });
        }
    };

    handleNewProductChange = (e) => {
        const value = e.target.value;
        this.setState({
            newProduct: value,
            productForm: { ...this.state.productForm, name: value }
        });
    };

    handleCancelNewProduct = () => {
        this.setState({
            showAddProduct: false,
            newProduct: '',
            productForm: { ...this.state.productForm, name: '' }
        });
    };

    handleProductFormChange = (field, value) => {
        this.setState(prev => {
            const newForm = {
                ...prev.productForm,
                [field]: value
            };

            // Reset brand and product name when category changes (cascading selection)
            if (field === 'category_id') {
                newForm.brand = '';
                newForm.name = '';
            }

            return {
                productForm: newForm,
                // Also reset add brand/product mode when category changes
                showAddBrand: field === 'category_id' ? false : prev.showAddBrand,
                newBrand: field === 'category_id' ? '' : prev.newBrand,
                showAddProduct: field === 'category_id' ? false : prev.showAddProduct,
                newProduct: field === 'category_id' ? '' : prev.newProduct
            };
        });
    };

    handleSaveProduct = async () => {
        const { editingProduct, productForm, categories, products } = this.state;

        if (!productForm.name.trim()) {
            alert('Product name is required');
            return;
        }

        // Check for duplicate product name + brand combination (case-insensitive) when creating new product
        if (!editingProduct) {
            const newName = productForm.name.trim().toLowerCase();
            const newBrand = (productForm.brand || '').trim().toLowerCase();
            const duplicate = products.find(p => {
                const existingName = (p.product_name || p.name || '').toLowerCase();
                const existingBrand = (p.brand || '').toLowerCase();
                return existingName === newName && existingBrand === newBrand;
            });
            if (duplicate) {
                const brandText = productForm.brand ? ` with brand "${productForm.brand.trim()}"` : '';
                alert(`A product named "${productForm.name.trim()}"${brandText} already exists.`);
                return;
            }
        }

        this.setState({ saving: true });

        try {
            // Get unitId from the selected category's default unit
            let unitId = null;
            if (productForm.category_id) {
                const selectedCategory = categories.find(c =>
                    String(c.category_id) === String(productForm.category_id)
                );
                if (selectedCategory) {
                    // Try both snake_case and camelCase field names
                    unitId = selectedCategory.default_unit_id || selectedCategory.defaultUnitId;
                }
            }

            // Build product data with proper types (using camelCase for API)
            const productData = {
                name: productForm.name.trim(),
                brand: productForm.brand || '',
                categoryId: parseInt(productForm.category_id, 10) || null,
                unitId: unitId ? parseInt(unitId, 10) : null,
                reorderPoint: parseInt(productForm.reorder_point, 10) || 0,
                costPerUnit: parseFloat(productForm.cost_per_unit) || 0,
                retailPrice: parseFloat(productForm.retail_price) || 0,
                wholesalePrice: parseFloat(productForm.wholesale_price) || 0,
                installerId: this.props.typeId
            };

            let savedProductId;
            if (editingProduct) {
                savedProductId = editingProduct.product_id || editingProduct.id;
                await InventoryAPI.updateProduct(savedProductId, productData);
                ReactGA.event({ category: 'Inventory', action: 'Updated Product', label: 'prod' });
            } else {
                const result = await InventoryAPI.createProduct(productData);
                savedProductId = result?.product_id || result?.id;
                ReactGA.event({ category: 'Inventory', action: 'Created Product', label: 'prod' });
            }

            // Evaluate reorder task after saving (fire-and-forget)
            if (savedProductId) {
                const locationId = this.props.selectedLocationId && this.props.selectedLocationId !== 'all'
                    ? this.props.selectedLocationId : null;
                InventoryAPI.evaluateReorder(savedProductId, this.props.typeId, locationId).catch(() => {});
            }

            await this.loadData();
            this.handleCloseProductModal();
        } catch (error) {
            console.error('Error saving product:', error);
            if (error.response && error.response.status === 409) {
                alert(error.response.data.message);
            } else {
                alert('Error saving product. Please try again.');
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    handleDeleteProduct = async () => {
        const { editingProduct } = this.state;

        if (!editingProduct) return;

        const productId = editingProduct.product_id || editingProduct.id;
        const productName = editingProduct.product_name || editingProduct.name || '';

        // Confirm deletion
        if (!window.confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) {
            return;
        }

        this.setState({ saving: true });

        try {
            await InventoryAPI.deleteProduct(productId, this.props.typeId);
            await this.loadData();
            this.handleCloseProductModal();
            ReactGA.event({ category: 'Inventory', action: 'Deleted Product', label: 'prod' });
        } catch (error) {
            console.error('Error deleting product:', error);
            const message = error.response?.data?.message || 'Error deleting product. Please try again.';
            alert(message);
        } finally {
            this.setState({ saving: false });
        }
    };

    // Check if a product can be deleted (no stock, no usage)
    canDeleteProduct = () => {
        const { editingProduct } = this.state;
        if (!editingProduct) return false;

        const quantity = parseFloat(editingProduct.quantity_on_hand || editingProduct.total_quantity || 0);
        const itemCount = parseInt(editingProduct.item_count) || 0;
        const usageCount = parseInt(editingProduct.usage_count) || 0;

        return quantity === 0 && itemCount === 0 && usageCount === 0;
    };

    // =========================================================================
    // Adjust Quantity Modal
    // =========================================================================

    handleOpenAdjustModal = (item, productId) => {
        this.setState({
            showAdjustModal: true,
            adjustingItem: { ...item, productId },
            adjustQuantity: item.quantity_remaining || ''
        });
    };

    handleCloseAdjustModal = () => {
        this.setState({
            showAdjustModal: false,
            adjustingItem: null,
            adjustQuantity: ''
        });
    };

    handleAdjustQuantityChange = (e) => {
        this.setState({ adjustQuantity: e.target.value });
    };

    handleSaveAdjustment = async () => {
        const { adjustingItem, adjustQuantity } = this.state;

        if (!adjustQuantity || parseFloat(adjustQuantity) < 0) {
            alert('Please enter a valid quantity');
            return;
        }

        this.setState({ saving: true });

        try {
            await InventoryAPI.adjustInventory({
                itemId: adjustingItem.item_id || adjustingItem.id,
                newQuantity: parseFloat(adjustQuantity)
            });

            // Reload items for this product
            const items = await InventoryAPI.getInventoryItems(adjustingItem.productId, this.props.typeId);

            this.setState(prev => ({
                productItems: {
                    ...prev.productItems,
                    [adjustingItem.productId]: items || []
                }
            }));

            // Reload main data to update totals
            await this.loadData();
            this.handleCloseAdjustModal();
            ReactGA.event({ category: 'Inventory', action: 'Adjusted Stock', label: 'prod' });
        } catch (error) {
            console.error('Error adjusting inventory:', error);
            alert('Error adjusting inventory. Please try again.');
        } finally {
            this.setState({ saving: false });
        }
    };

    // =========================================================================
    // Usage History Modal
    // =========================================================================

    handleOpenUsageModal = async (product) => {
        const productId = product.product_id || product.id;

        this.setState({
            showUsageModal: true,
            usageProduct: product,
            usageData: [],
            loadingUsage: true
        });

        try {
            const usageData = await InventoryAPI.getProductUsage(productId, this.props.typeId);
            this.setState({
                usageData: usageData || [],
                loadingUsage: false
            });
        } catch (error) {
            console.error('Error loading usage data:', error);
            this.setState({
                usageData: [],
                loadingUsage: false
            });
        }
    };

    handleCloseUsageModal = () => {
        this.setState({
            showUsageModal: false,
            usageProduct: null,
            usageData: [],
            loadingUsage: false
        });
    };

    handleOpenItemUsageModal = async (item, product) => {
        const itemId = item.item_id || item.id;

        this.setState({
            showUsageModal: true,
            usageProduct: {
                ...product,
                // Override with item-specific info for display
                isItemLevel: true,
                barcode: item.barcode,
                item_id: itemId
            },
            usageData: [],
            loadingUsage: true
        });

        try {
            const usageData = await InventoryAPI.getItemUsage(itemId, this.props.typeId);
            this.setState({
                usageData: usageData || [],
                loadingUsage: false
            });
        } catch (error) {
            console.error('Error loading item usage data:', error);
            this.setState({
                usageData: [],
                loadingUsage: false
            });
        }
    };

    // =========================================================================
    // CSV Export
    // =========================================================================

    handleExportCSV = async () => {
        const { products } = this.state;

        if (products.length === 0) {
            alert('No products to export');
            return;
        }

        this.setState({ saving: true });

        try {
            // Fetch all items for all products
            const allData = [];

            for (const product of products) {
                const productId = product.product_id || product.id;
                const productName = product.product_name || product.name || '';
                const brand = product.brand || '';
                const category = product.category_name || '';
                const quantityOnHand = parseFloat(product.quantity_on_hand || product.total_quantity || 0).toFixed(2);
                const allocated = parseFloat(this.state.allocatedMap[productId] || 0).toFixed(2);
                const available = (parseFloat(quantityOnHand) - parseFloat(allocated)).toFixed(2);
                const reorderPoint = product.reorder_point || '';
                const usageCount = product.usage_count || 0;

                // Fetch items for this product
                let items = [];
                try {
                    items = await InventoryAPI.getInventoryItems(productId, this.props.typeId);
                } catch (err) {
                    console.error(`Error fetching items for product ${productId}:`, err);
                }

                if (items && items.length > 0) {
                    // Add a row for each item
                    for (const item of items) {
                        allData.push({
                            category,
                            brand,
                            productName,
                            quantityOnHand,
                            allocated,
                            available,
                            reorderPoint,
                            usageCount,
                            barcode: item.barcode || '',
                            amountRemaining: parseFloat(item.quantity_remaining || 0).toFixed(2),
                            dateReceived: item.created_at ? convertFromUTC(item.created_at).format('MMM D, YYYY') : ''
                        });
                    }
                } else {
                    // Add a row for the product even if no items
                    allData.push({
                        category,
                        brand,
                        productName,
                        quantityOnHand,
                        allocated,
                        available,
                        reorderPoint,
                        usageCount,
                        barcode: '',
                        amountRemaining: '',
                        dateReceived: ''
                    });
                }
            }

            // Generate CSV content
            const multiLoc = isMultiLocation(this.props.locations);
            const selectedLoc = multiLoc && this.props.locations
                ? this.props.locations.find(l => String(l.location_id) === String(this.props.selectedLocationId))
                : null;
            const locationName = selectedLoc ? getLocationName(selectedLoc) : 'All Locations';

            const headers = [
                'Category',
                'Brand',
                'Product Name',
                'Total In Stock',
                'Allocated',
                'Available',
                'Reorder Point',
                'Jobs Used',
                'Item Barcode',
                'Amount Remaining',
                'Date Received',
                ...(multiLoc ? ['Location'] : [])
            ];

            const csvRows = [headers.join(',')];

            for (const row of allData) {
                const values = [
                    `"${row.category}"`,
                    `"${row.brand}"`,
                    `"${row.productName}"`,
                    row.quantityOnHand,
                    row.allocated,
                    row.available,
                    row.reorderPoint,
                    row.usageCount,
                    `"${row.barcode}"`,
                    row.amountRemaining,
                    `"${row.dateReceived}"`,
                    ...(multiLoc ? [`"${locationName}"`] : [])
                ];
                csvRows.push(values.join(','));
            }

            const csvContent = csvRows.join('\n');

            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `inventory_export_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Error exporting inventory. Please try again.');
        } finally {
            this.setState({ saving: false });
        }
    };

    // =========================================================================
    // Render Methods
    // =========================================================================

    renderOverviewCards() {
        const { totalProducts, lowStockCount, totalItems } = this.getMetrics();

        return (
            <div className="overview-cards">
                <div className={`overview-card ${lowStockCount > 0 ? 'warning' : ''}`}>
                    <div className="number">{lowStockCount}</div>
                    <div className="label">Low Stock</div>
                </div>
                <div className="overview-card">
                    <div className="number">{totalProducts}</div>
                    <div className="label">Products</div>
                </div>
                <div className="overview-card">
                    <div className="number">{totalItems}</div>
                    <div className="label">Total Items</div>
                </div>
            </div>
        );
    }

    renderFiltersSection() {
        const { searchTerm, selectedCategoryFilter, categories } = this.state;

        const categoryOptions = ["All Categories", ...new Set(
            categories.map(c => c.category_name || c.name).filter(Boolean)
        )].sort((a, b) => a === "All Categories" ? -1 : b === "All Categories" ? 1 : a.localeCompare(b));

        return (
            <div className="filters-section">
                <div className="search-controls">
                    <div className="search-group">
                        <label htmlFor="productSearch">Search:</label>
                        <div className="search-input-group">
                            <input
                                type="text"
                                className="search-input"
                                id="productSearch"
                                placeholder="Search by name, brand, or SKU..."
                                value={searchTerm}
                                onChange={this.handleSearchChange}
                            />
                            {searchTerm && (
                                <button
                                    className="clear-search-btn"
                                    onClick={this.clearSearch}
                                    type="button"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="filter-controls">
                    <div className="filter-group">
                        <label>Category:</label>
                        <select
                            className="form-control"
                            value={selectedCategoryFilter}
                            onChange={this.handleCategoryFilterChange}
                        >
                            {categoryOptions.map((opt, idx) => (
                                <option key={idx} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    // Role-based permission helpers
    getRoleFlags = () => {
        const userRole = this.props.user?.role;
        const isMultiLoc = isMultiLocation(this.props.locations);
        const allLocs = checkIsAllLocations(this.props.selectedLocationId);
        const isReadOnly = (isMultiLoc && allLocs) || isTechnician(userRole);
        // Products are catalog definitions (installer-scoped), not inventory (location-scoped),
        // so managing them is allowed regardless of the location filter.
        const canManageProducts = !isTechnician(userRole);
        const canAdjust = !isReadOnly && !isTechnician(userRole);
        const canTransfer = isMultiLoc && !allLocs && userRole !== 'viewer';
        const canExport = !isTechnician(userRole);
        return { userRole, isMultiLoc, allLocs, isReadOnly, canManageProducts, canAdjust, canTransfer, canExport };
    };

    handleOpenTransferModal = (item, product) => {
        this.setState({
            showTransferModal: true,
            transferItem: { ...item, product_name: product.product_name || product.name, brand: product.brand }
        });
    };

    handleCloseTransferModal = () => {
        this.setState({ showTransferModal: false, transferItem: null });
    };

    handleTransferSuccess = async () => {
        await this.loadData();
        // Clear expanded rows cache so items reload
        this.setState({ productItems: {}, expandedRows: [] });
        ReactGA.event({ category: 'Inventory', action: 'Transferred Inventory', label: 'prod' });
    };

    // Demand preview
    handleOpenDemandDrawer = (product) => {
        this.setState({
            showDemandDrawer: true,
            demandProduct: product
        });
    };

    handleCloseDemandDrawer = () => {
        this.setState({ showDemandDrawer: false, demandProduct: null });
    };

    renderActionButtons() {
        const { saving } = this.state;
        const { canManageProducts, canExport } = this.getRoleFlags();

        return (
            <div className="action-buttons">
                <div className="label">Quick Actions:</div>
                <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={() => this.setState({ showReceiveModal: true })}>
                    <i className="fa-sharp fa-regular fa-plus mr-2"></i>
                    Receive Inventory
                </button>
                {canManageProducts && (
                    <button className="jv-btn jv-btn-tertiary jv-btn-sm" onClick={this.handleOpenAddProduct}>
                        <i className="fa-sharp fa-regular fa-cube mr-2"></i>
                        Add Product
                    </button>
                )}
                {canExport && (
                    <button
                        className="jv-btn jv-btn-tertiary jv-btn-sm"
                        onClick={this.handleExportCSV}
                        disabled={saving}
                    >
                        <i className={`fa-sharp fa-regular ${saving ? 'fa-spinner fa-spin' : 'fa-download'} mr-2`}></i>
                        {saving ? 'Exporting...' : 'Export CSV'}
                    </button>
                )}
            </div>
        );
    }

    // Helper to get unit name from unit_id
    getUnitName = (unitId) => {
        const { units } = this.state;
        const unit = units.find(u => String(u.unit_id) === String(unitId));
        return unit ? unit.unit_name : '';
    };

    // Helper to get short unit label for a product row (e.g. "ft", "sq ft")
    getProductUnitLabel = (row) => {
        const { categories, units } = this.state;
        const categoryId = row.category_id;
        if (!categoryId) return '';
        const category = categories.find(c => String(c.category_id) === String(categoryId));
        if (!category) return '';
        const unitId = category.default_unit_id || category.defaultUnitId;
        if (!unitId) return '';
        const unit = units.find(u => String(u.unit_id) === String(unitId));
        return unit ? (unit.unit_code || unit.unit_name || '') : '';
    };

    renderProductModal() {
        const { showProductModal, editingProduct, productForm, categories, saving, showAddBrand, newBrand, showAddProduct, newProduct } = this.state;
        // Filter brands by selected category (cascading selection)
        const existingBrands = this.getUniqueBrands(productForm.category_id);
        // Filter product names by selected category and brand
        const existingProductNames = this.getUniqueProductNames(productForm.category_id, productForm.brand);

        // Get unit name from the selected category's default unit
        let selectedUnitName = '';
        if (productForm.category_id) {
            const selectedCategory = categories.find(c => String(c.category_id) === String(productForm.category_id));
            if (selectedCategory) {
                // Try both snake_case and camelCase field names
                const defaultUnitId = selectedCategory.default_unit_id || selectedCategory.defaultUnitId;
                if (defaultUnitId) {
                    selectedUnitName = this.getUnitName(defaultUnitId);
                }
            }
        }

        // Determine if brand is selected (either from dropdown or new brand input)
        const hasBrandSelected = productForm.brand && productForm.brand.trim();

        return (
            <Modal show={showProductModal} onHide={this.handleCloseProductModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingProduct ? 'Edit Product' : 'Add Product'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {/* Category Selection - First */}
                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Control
                                as="select"
                                value={productForm.category_id}
                                onChange={(e) => this.handleProductFormChange('category_id', e.target.value)}
                            >
                                <option value="">Select category...</option>
                                {/* Deduplicate by category_id and sort alphabetically */}
                                {[...new Map(categories.map(c => [c.category_id, c])).values()]
                                    .sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''))
                                    .map(cat => (
                                        <option key={cat.category_id} value={cat.category_id}>
                                            {cat.category_name}
                                        </option>
                                    ))}
                            </Form.Control>
                        </Form.Group>

                        {/* Brand Selection - filtered by category */}
                        <Form.Group className="mb-3">
                            <Form.Label>Brand</Form.Label>
                            {!showAddBrand ? (
                                <Form.Control
                                    as="select"
                                    value={productForm.brand}
                                    onChange={this.handleBrandChange}
                                    disabled={!productForm.category_id}
                                >
                                    <option value="">
                                        {!productForm.category_id
                                            ? 'Select a category first...'
                                            : existingBrands.length === 0
                                                ? 'No brands for this category - add new'
                                                : 'Select a brand...'}
                                    </option>
                                    {existingBrands.map(brand => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                    {productForm.category_id && (
                                        <option value="add_new">Add New Brand</option>
                                    )}
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control
                                        type="text"
                                        value={newBrand}
                                        onChange={this.handleNewBrandChange}
                                        placeholder="Enter new brand name"
                                        autoFocus
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ml-2"
                                        onClick={this.handleCancelNewBrand}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </Form.Group>

                        {/* Product Name - filtered by category and brand */}
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name *</Form.Label>
                            {!showAddProduct ? (
                                <Form.Control
                                    as="select"
                                    value={productForm.name}
                                    onChange={this.handleProductNameChange}
                                    disabled={!hasBrandSelected}
                                >
                                    <option value="">
                                        {!productForm.category_id
                                            ? 'Select a category first...'
                                            : !hasBrandSelected
                                                ? 'Select a brand first...'
                                                : existingProductNames.length === 0
                                                    ? 'No products for this brand - add new'
                                                    : 'Select a product...'}
                                    </option>
                                    {existingProductNames.map(name => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                    {hasBrandSelected && (
                                        <option value="add_new">Add New Product</option>
                                    )}
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control
                                        type="text"
                                        value={newProduct}
                                        onChange={this.handleNewProductChange}
                                        placeholder="Enter new product name"
                                        autoFocus
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ml-2"
                                        onClick={this.handleCancelNewProduct}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Low Stock Alert
                                {selectedUnitName && <small className="text-muted ml-1">({selectedUnitName})</small>}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                value={productForm.reorder_point}
                                onChange={(e) => this.handleProductFormChange('reorder_point', e.target.value)}
                                placeholder="0"
                                style={{ maxWidth: '200px' }}
                            />
                            <Form.Text className="text-muted">
                                Alert when total stock falls below this amount
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Cost</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={productForm.cost_per_unit}
                                onChange={(e) => this.handleProductFormChange('cost_per_unit', e.target.value)}
                                placeholder="0.00"
                                style={{ maxWidth: '200px' }}
                            />
                            <Form.Text className="text-muted">
                                Default unit cost. Receive flow updates per-batch cost on inventory.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Retail Price</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={productForm.retail_price}
                                onChange={(e) => this.handleProductFormChange('retail_price', e.target.value)}
                                placeholder="0.00"
                                style={{ maxWidth: '200px' }}
                            />
                            <Form.Text className="text-muted">
                                What retail customers pay. Leave 0 if not sold to retail.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Wholesale Price</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={productForm.wholesale_price}
                                onChange={(e) => this.handleProductFormChange('wholesale_price', e.target.value)}
                                placeholder="0.00"
                                style={{ maxWidth: '200px' }}
                            />
                            <Form.Text className="text-muted">
                                What wholesale customers pay. Leave 0 if not sold wholesale.
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div>
                        {editingProduct && this.canDeleteProduct() && (
                            <Button
                                variant="outline-danger"
                                onClick={this.handleDeleteProduct}
                                disabled={saving}
                            >
                                <i className="fa-sharp fa-regular fa-trash mr-2"></i>
                                Delete Product
                            </Button>
                        )}
                    </div>
                    <div>
                        <Button variant="secondary" onClick={this.handleCloseProductModal} className="mr-2">
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={this.handleSaveProduct}
                            disabled={saving}
                        >
                            {saving ? (
                                <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Saving...</>
                            ) : (
                                'Save Product'
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }

    renderAdjustModal() {
        const { showAdjustModal, adjustingItem, adjustQuantity, saving } = this.state;

        if (!adjustingItem) return null;

        return (
            <Modal show={showAdjustModal} onHide={this.handleCloseAdjustModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Adjust Quantity</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <small className="text-muted">Barcode:</small>
                        <div><code>{adjustingItem.barcode}</code></div>
                    </div>
                    <Form.Group>
                        <Form.Label>Amount Remaining</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={adjustQuantity}
                            onChange={this.handleAdjustQuantityChange}
                            placeholder="Enter new quantity"
                            autoFocus
                        />
                        <Form.Text className="text-muted">
                            Current: {parseFloat(adjustingItem.quantity_remaining || 0).toFixed(2)}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleCloseAdjustModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={this.handleSaveAdjustment}
                        disabled={saving}
                    >
                        {saving ? (
                            <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Saving...</>
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderUsageModal() {
        const { showUsageModal, usageProduct, usageData, loadingUsage } = this.state;

        if (!usageProduct) return null;

        const productName = usageProduct.product_name || usageProduct.name || '';
        const brand = usageProduct.brand || '';
        const displayName = brand ? `${brand} - ${productName}` : productName;
        const isItemLevel = usageProduct.isItemLevel;

        return (
            <Modal show={showUsageModal} onHide={this.handleCloseUsageModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Usage History {isItemLevel && '- Item'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="usage-product-info mb-3">
                        <strong>{displayName}</strong>
                        {isItemLevel && (
                            <div className="mt-1">
                                <code>{usageProduct.barcode}</code>
                            </div>
                        )}
                        <div className="text-muted small">
                            {usageProduct.category_name || 'Uncategorized'}
                        </div>
                    </div>

                    {loadingUsage ? (
                        <div className="text-center py-4">
                            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                            <p className="mt-2">Loading usage history...</p>
                        </div>
                    ) : usageData.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <i className="fa-sharp fa-regular fa-inbox fa-2x mb-2"></i>
                            <p>No usage records found for this product.</p>
                        </div>
                    ) : (
                        <div className="usage-table-container">
                            <table className="jv-table jv-table-sm">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Customer</th>
                                        <th>Vehicle</th>
                                        <th>Service</th>
                                        <th>Qty Used</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usageData.map((record, index) => (
                                        <tr key={index}>
                                            <td>
                                                {record.usage_date
                                                    ? convertFromUTC(record.usage_date).format('MMM D, YYYY')
                                                    : '-'}
                                            </td>
                                            <td>{record.client_name || '-'}</td>
                                            <td>{record.vehicle_info || '-'}</td>
                                            <td>{record.service_description || '-'}</td>
                                            <td>
                                                {parseFloat(record.quantity_used || 0).toFixed(2)}
                                                {record.unit_code && <small className="text-muted ml-1">{record.unit_code}</small>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleCloseUsageModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { isDesktop, error, loading, productItems } = this.state;

        if (error) {
            return (
                <div className="inventory-container">
                    <div className="error-message">
                        <h3>Error Loading Inventory</h3>
                        <p>{error}</p>
                        <button onClick={() => window.location.reload()}>Refresh Page</button>
                    </div>
                </div>
            );
        }

        if (loading) {
            return (
                <div className="inventory-container">
                    <div className="text-center py-5">
                        <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                        <p className="mt-3">Loading inventory...</p>
                    </div>
                </div>
            );
        }

        // Normalize product data to handle different API field names
        // Include _expanded flag so react-bootstrap-table re-renders the cell when expand state changes
        const filteredProducts = this.getFilteredProducts().map(p => {
            const pid = p.product_id || p.id;
            return {
                ...p,
                id: pid,
                name: p.product_name || p.name,
                brand: p.brand || '',
                _expanded: this.state.expandedRows.includes(pid)
            };
        });

        const { isMultiLoc, allLocs, canManageProducts, canTransfer } = this.getRoleFlags();

        const columns = [
            {
                dataField: '_expanded',
                text: '',
                formatter: (cell, row) => {
                    const hasItems = parseInt(row.item_count) > 0;
                    return hasItems ? (
                        <span
                            className="expand-toggle"
                            onClick={(e) => { e.stopPropagation(); this.handleToggleExpand(row.id); }}
                        >
                            <i className={`fa-sharp fa-regular ${cell ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                        </span>
                    ) : null;
                },
                headerStyle: { width: '40px' }
            },
            {
                dataField: 'category_name',
                text: 'Category'
            },
            {
                dataField: 'name',
                text: 'Product Name',
                formatter: (cell, row) => {
                    const productName = row.product_name || row.name || '';
                    const brand = row.brand || '';
                    return brand ? `${brand} - ${productName}` : productName;
                }
            },
            {
                dataField: 'quantity_on_hand',
                text: 'In Stock',
                formatter: (cell, row) => {
                    // Handle different possible field names from API
                    const qty = parseFloat(row.quantity_on_hand || row.total_quantity || row.quantity_in_stock || row.stock_quantity || row.quantity || cell) || 0;
                    const itemCount = parseInt(row.item_count) || 0;
                    const reorder = parseFloat(row.reorder_point) || 0;
                    const rowId = row.product_id || row.id;
                    const allocated = parseFloat(this.state.allocatedMap[rowId] || 0);
                    const available = qty - allocated;
                    const isLow = available <= 0 || (reorder > 0 && available <= reorder);
                    const unitLabel = this.getProductUnitLabel(row);
                    return (
                        <div>
                            <span className={isLow ? 'low-stock-warning' : ''}>
                                {qty.toFixed(2)}{unitLabel && <small className="text-muted ml-1">{unitLabel}</small>} <small className="text-muted">({itemCount})</small>
                                {isLow && <i className="fa-sharp fa-regular fa-exclamation-triangle ml-2"></i>}
                            </span>
                            {allocated > 0 && (
                                <div><small className="text-warning">{allocated.toFixed(2)}{unitLabel && ` ${unitLabel}`} allocated</small></div>
                            )}
                        </div>
                    );
                }
            },
            {
                dataField: 'available',
                text: 'Available',
                formatter: (cell, row) => {
                    const qty = parseFloat(row.quantity_on_hand || row.total_quantity || row.quantity_in_stock || row.stock_quantity || row.quantity) || 0;
                    const rowId = row.product_id || row.id;
                    const allocated = parseFloat(this.state.allocatedMap[rowId] || 0);
                    const available = qty - allocated;
                    const reorder = parseFloat(row.reorder_point) || 0;
                    const isLow = available <= 0 || (reorder > 0 && available <= reorder);
                    const unitLabel = this.getProductUnitLabel(row);
                    return (
                        <span className={isLow ? 'low-stock-warning' : ''}>
                            {available.toFixed(2)}{unitLabel && <small className="text-muted ml-1">{unitLabel}</small>}
                        </span>
                    );
                }
            },
            {
                dataField: 'reorder_point',
                text: 'Reorder Point',
                formatter: (cell, row) => {
                    if (!cell) return '-';
                    const unitLabel = this.getProductUnitLabel(row);
                    return (
                        <span>
                            {parseFloat(cell).toFixed(0)}{unitLabel && <small className="text-muted ml-1">{unitLabel}</small>}
                        </span>
                    );
                }
            },
            {
                dataField: 'cost_per_unit',
                text: 'Cost',
                formatter: (cell) => {
                    const price = parseFloat(cell) || 0;
                    return price > 0 ? `$${price.toFixed(2)}` : '-';
                }
            },
            {
                dataField: 'retail_price',
                text: 'Retail',
                formatter: (cell) => {
                    const price = parseFloat(cell) || 0;
                    return price > 0 ? `$${price.toFixed(2)}` : '-';
                }
            },
            {
                dataField: 'wholesale_price',
                text: 'Wholesale',
                formatter: (cell) => {
                    const price = parseFloat(cell) || 0;
                    return price > 0 ? `$${price.toFixed(2)}` : '-';
                }
            },
            // Row actions — demand preview, create PO, edit product
            {
                dataField: 'actions',
                text: '',
                formatter: (cell, row) => {
                    const productId = row.product_id || row.id;
                    return (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <button
                                className="jv-btn-icon"
                                onClick={(e) => { e.stopPropagation(); this.handleOpenDemandDrawer({ ...row, id: productId }); }}
                                title="View demand & stock position"
                            >
                                <i className="fa-sharp fa-regular fa-chart-line"></i>
                            </button>
                            {canManageProducts && (
                                <button
                                    className="jv-btn-icon"
                                    onClick={(e) => { e.stopPropagation(); this.handleOpenEditProduct(row); }}
                                    title="Edit product"
                                >
                                    <i className="fa-sharp fa-regular fa-pencil"></i>
                                </button>
                            )}
                        </div>
                    );
                },
                headerStyle: { width: canManageProducts ? '90px' : '50px' }
            }
        ];

        const { canAdjust: canAdjustItems } = this.getRoleFlags();

        const expandRow = {
            renderer: (row) => {
                const rowId = row.product_id || row.id;
                const items = productItems[rowId] || [];

                if (items.length === 0) {
                    return (
                        <div className="inventory-items-details">
                            <p className="text-muted text-center">No items found for this product</p>
                        </div>
                    );
                }

                const showActionCol = canAdjustItems || canTransfer;
                const showLocationCol = isMultiLoc && allLocs;

                return (
                    <div className="inventory-items-details">
                        <div className="items-header">
                            <strong>Inventory Items ({items.length})</strong>
                        </div>
                        <table className="jv-table jv-table-sm">
                            <thead>
                                <tr>
                                    <th>Barcode</th>
                                    {showLocationCol && <th>Location</th>}
                                    <th>Amount Remaining</th>
                                    <th>Date Received</th>
                                    <th>Usage</th>
                                    {showActionCol && <th style={{ width: canTransfer && canAdjustItems ? '100px' : '60px' }}></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => {
                                    const usageCount = parseInt(item.usage_count) || 0;
                                    const itemLocId = item.location_id || item.locationId;
                                    const itemLoc = this.props.locations?.find(l => String(l.location_id) === String(itemLocId));
                                    const itemLocName = itemLoc ? getLocationName(itemLoc) : (item.location_name || 'Unknown');
                                    return (
                                        <tr key={index}>
                                            <td><code>{item.barcode}</code></td>
                                            {showLocationCol && (
                                                <td>
                                                    <span className="jv-location-badge" style={{ backgroundColor: getLocationBadgeColor(itemLocId, this.props.locations) }}>
                                                        {itemLocName}
                                                    </span>
                                                </td>
                                            )}
                                            <td>{parseFloat(item.quantity_remaining || 0).toFixed(2)}</td>
                                            <td>{item.created_at ? convertFromUTC(item.created_at).format('MMM D, YYYY') : '-'}</td>
                                            <td>
                                                {usageCount === 0 ? (
                                                    <span className="text-muted">No usage</span>
                                                ) : (
                                                    <a
                                                        href="#/"
                                                        className="usage-link"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            this.handleOpenItemUsageModal(item, row);
                                                        }}
                                                    >
                                                        {usageCount} {usageCount === 1 ? 'job' : 'jobs'}
                                                    </a>
                                                )}
                                            </td>
                                            {showActionCol && (
                                                <td>
                                                    <div style={{ display: 'flex', gap: '4px' }}>
                                                        {canAdjustItems && (
                                                            <button
                                                                className="jv-btn-icon"
                                                                onClick={() => this.handleOpenAdjustModal(item, rowId)}
                                                                title="Adjust quantity"
                                                            >
                                                                <i className="fa-sharp fa-regular fa-pencil"></i>
                                                            </button>
                                                        )}
                                                        {canTransfer && parseFloat(item.quantity_remaining) > 0 && (
                                                            <button
                                                                className="jv-btn-icon"
                                                                onClick={() => this.handleOpenTransferModal(item, row)}
                                                                title="Transfer to another location"
                                                            >
                                                                <i className="fa-sharp fa-regular fa-arrow-right-arrow-left"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            },
            expanded: this.state.expandedRows,
            showExpandColumn: false,
            expandByColumnOnly: true
        };

        const rowClasses = (row) => {
            const qty = parseFloat(row.quantity_on_hand || row.total_quantity || row.quantity_in_stock || row.stock_quantity || row.quantity) || 0;
            const reorder = parseFloat(row.reorder_point) || 0;
            const rowId = row.product_id || row.id;
            const allocated = parseFloat(this.state.allocatedMap[rowId] || 0);
            const available = qty - allocated;
            if (available <= 0 || (reorder > 0 && available <= reorder)) {
                return 'low-stock-row';
            }
            return '';
        };

        return (
            <React.Fragment>
                <div className={`inventory-container ${isDesktop ? 'desktop' : 'mobile'}`}>
                    {this.renderOverviewCards()}
                    {this.renderFiltersSection()}
                    {this.renderActionButtons()}

                    <div className="table-container jv-bt-table">
                        <BootstrapTable
                            keyField='id'
                            data={filteredProducts}
                            columns={columns}
                            expandRow={expandRow}
                            rowClasses={rowClasses}
                            noDataIndication="No products found"
                        />
                    </div>
                </div>

                {this.renderProductModal()}
                {this.renderAdjustModal()}
                {this.renderUsageModal()}
                <TransferInventoryModal
                    show={this.state.showTransferModal}
                    onHide={this.handleCloseTransferModal}
                    onSuccess={this.handleTransferSuccess}
                    item={this.state.transferItem}
                    locations={this.props.locations}
                    currentLocationId={this.props.selectedLocationId}
                    installerId={this.props.typeId}
                />

                {this.state.showDemandDrawer && this.state.demandProduct && (
                    <DemandPreviewDrawer
                        show={true}
                        onHide={this.handleCloseDemandDrawer}
                        productId={this.state.demandProduct.product_id || this.state.demandProduct.id}
                        productName={this.state.demandProduct.brand
                            ? `${this.state.demandProduct.brand} - ${this.state.demandProduct.product_name || this.state.demandProduct.name}`
                            : (this.state.demandProduct.product_name || this.state.demandProduct.name)}
                        installerId={this.props.typeId}
                        locationId={this.props.selectedLocationId}
                    />
                )}

                {this.state.showReceiveModal && (
                    <ReceiveInventory
                        show={true}
                        onHide={() => this.setState({ showReceiveModal: false })}
                        onSuccess={() => { this.setState({ showReceiveModal: false }); this.loadData(); }}
                        typeId={this.props.typeId}
                        selectedLocationId={this.props.selectedLocationId}
                        locations={this.props.locations}
                    />
                )}

                <style>{`
                    .inventory-container {
                        max-width: 1400px;
                        margin: 0 auto;
                        padding: var(--space-6);
                        background: transparent;
                        min-height: 100vh;
                    }
                    .inventory-container.mobile { padding: var(--space-4); }

                    .error-message {
                        background: var(--color-bg-card);
                        border: 1px solid var(--color-danger-light);
                        border-radius: var(--radius-md);
                        padding: var(--space-6);
                        text-align: center;
                        color: var(--color-danger);
                    }
                    .error-message button {
                        background: var(--color-danger);
                        color: white;
                        border: none;
                        padding: var(--space-2) var(--space-4);
                        border-radius: var(--radius-sm);
                        cursor: pointer;
                        margin-top: var(--space-3);
                    }

                    .overview-cards {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: var(--space-4);
                        margin-bottom: var(--space-6);
                    }
                    .mobile .overview-cards {
                        grid-template-columns: repeat(3, 1fr);
                        gap: var(--space-3);
                    }
                    .overview-card {
                        background: var(--color-bg-card);
                        border: 1px solid var(--color-border);
                        border-radius: var(--radius-md);
                        padding: var(--space-5);
                        text-align: center;
                        box-shadow: var(--shadow-sm);
                    }
                    .overview-card.warning .number { color: var(--color-warning); }
                    .overview-card .number {
                        font-size: var(--font-size-xl);
                        font-weight: var(--font-weight-bold);
                        color: var(--color-text-primary);
                        margin-bottom: var(--space-1);
                    }
                    .overview-card .label {
                        font-size: var(--font-size-xs);
                        color: var(--color-text-secondary);
                        text-transform: uppercase;
                        letter-spacing: var(--letter-spacing-uppercase);
                        font-weight: var(--font-weight-semibold);
                    }

                    .filters-section {
                        background: var(--color-bg-card);
                        border: 1px solid var(--color-border);
                        border-radius: var(--radius-md);
                        padding: var(--space-5);
                        margin-bottom: var(--space-4);
                        box-shadow: var(--shadow-sm);
                        display: flex;
                        gap: var(--space-6);
                        flex-wrap: wrap;
                        align-items: flex-end;
                    }
                    .search-controls { flex: 1; min-width: 250px; }
                    .search-group label {
                        font-size: var(--font-size-base);
                        font-weight: var(--font-weight-medium);
                        color: var(--color-text-primary);
                        display: block;
                        margin-bottom: var(--space-1);
                    }
                    .search-input-group {
                        position: relative;
                        display: flex;
                        align-items: center;
                    }
                    .search-input {
                        width: 100%;
                        padding: var(--space-2) var(--space-3);
                        border: 1px solid var(--color-border);
                        border-radius: var(--radius-md);
                        font-size: var(--font-size-base);
                    }
                    .search-input:focus {
                        outline: none;
                        border-color: var(--color-brand-teal);
                        box-shadow: 0 0 0 2px rgba(63, 178, 190, 0.1);
                    }
                    .clear-search-btn {
                        position: absolute;
                        right: 8px;
                        background: none;
                        border: none;
                        font-size: 18px;
                        color: var(--color-text-secondary);
                        cursor: pointer;
                    }
                    .filter-controls { display: flex; gap: var(--space-4); }
                    .filter-group label {
                        font-size: var(--font-size-base);
                        font-weight: var(--font-weight-medium);
                        color: var(--color-text-primary);
                        display: block;
                        margin-bottom: var(--space-1);
                    }
                    .filter-group select { min-width: 180px; }

                    .action-buttons {
                        background: var(--color-bg-card);
                        border: 1px solid var(--color-border);
                        border-radius: var(--radius-md);
                        padding: var(--space-4);
                        margin-bottom: var(--space-6);
                        display: flex;
                        align-items: center;
                        gap: var(--space-3);
                        flex-wrap: wrap;
                    }
                    .action-buttons .label {
                        font-size: var(--font-size-base);
                        font-weight: var(--font-weight-medium);
                        color: var(--color-text-primary);
                    }

                    .table-container {
                        background: var(--color-bg-card);
                        border: 1px solid var(--color-border);
                        border-radius: var(--radius-md);
                        overflow: hidden;
                        box-shadow: var(--shadow-sm);
                    }
                    .table-container .react-bootstrap-table table th {
                        background: var(--color-bg-table-header);
                        border-bottom: 2px solid var(--color-border);
                        border-top: none;
                        padding: 12px 16px;
                        font-weight: 600;
                        color: var(--color-gray-500);
                        font-size: 12px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .table-container .react-bootstrap-table table td {
                        padding: var(--space-3) var(--space-4);
                        border-bottom: 1px solid var(--color-border-light);
                        border-top: none;
                        vertical-align: middle;
                    }
                    .table-container .react-bootstrap-table table tbody tr:hover {
                        background-color: var(--color-bg-hover);
                    }

                    .low-stock-row { background-color: var(--color-warning-light) !important; }
                    .low-stock-warning { color: var(--color-warning-dark); font-weight: var(--font-weight-semibold); }

                    .expand-toggle {
                        cursor: pointer;
                        color: var(--color-text-secondary);
                        font-size: var(--font-size-xs);
                        padding: var(--space-1);
                    }
                    .expand-toggle:hover { color: var(--color-text-primary); }

                    .inventory-items-details {
                        background: var(--color-bg-panel);
                        padding: var(--space-4) var(--space-5);
                        border-left: 4px solid var(--color-brand-teal);
                    }
                    .items-header {
                        margin-bottom: var(--space-3);
                        color: var(--color-text-primary);
                        font-size: var(--font-size-base);
                    }
                    /* items-table styles now handled by jv-table jv-table-sm */

                    .usage-link {
                        color: var(--color-brand-teal);
                        text-decoration: none;
                        font-weight: var(--font-weight-medium);
                    }
                    .usage-link:hover { color: var(--color-brand-teal-hover); text-decoration: underline; }

                    .usage-product-info {
                        padding: var(--space-3) var(--space-4);
                        background: var(--color-bg-panel);
                        border-radius: var(--radius-md);
                        border-left: 4px solid var(--color-brand-teal);
                    }
                    /* usage-table styles now handled by jv-table jv-table-sm */
                    .usage-table-container { max-height: 400px; overflow-y: auto; }
                    .usage-table-container thead th { position: sticky; top: 0; }

                    @media (max-width: 768px) {
                        .filters-section { flex-direction: column; gap: var(--space-4); }
                        .filter-controls { width: 100%; }
                        .filter-group { flex: 1; }
                        .filter-group select { width: 100%; min-width: auto; }
                        .action-buttons { flex-direction: column; align-items: stretch; }
                    }
                `}</style>
            </React.Fragment>
        );
    }
}

export default InventoryDashboard;
